import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Mic, BookOpen, Anchor, Archive, Activity, Check, ArrowRight } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

const serif = { fontFamily: 'Cormorant Garamond, serif' };

const FEATURES = [
  {
    id: 'voice',
    icon: Mic,
    eyebrow: 'Live voice coaching',
    title: 'Think out loud with someone who truly listens.',
    body: 'Hold to talk, or go fully hands-free and let the conversation flow. SAGE answers in a warm, natural voice. No typing, no forms, just you and the thing that matters today.',
    moment: '"I have a hard conversation tomorrow." Ten minutes later you have the words, and the calm to say them.',
  },
  {
    id: 'learn',
    icon: BookOpen,
    eyebrow: 'Learn Mode',
    title: 'A tutor that asks better questions.',
    body: 'Name anything you want to master. SAGE builds a stage-by-stage plan and teaches the Socratic way, drawing understanding out of you instead of lecturing at you.',
    moment: 'Finish a stage and take home a clean lesson summary as a PDF or Markdown file.',
  },
  {
    id: 'anchors',
    icon: Anchor,
    eyebrow: 'Knowledge Anchors & memory',
    title: 'Coaching grounded in your wisdom.',
    body: 'Bring the books, teachers and ideas that shape you. SAGE weaves them into every session and remembers what you said last week, so you never start from zero.',
    moment: 'It greets you by name and picks up right where you left off.',
  },
  {
    id: 'archive',
    icon: Archive,
    eyebrow: 'Session archive & search',
    title: 'Every breakthrough, kept.',
    body: 'Insights, wins and commitments from each session are saved automatically. Search across months of growth and watch your own path come into focus.',
    moment: 'Type "confidence" and see every moment you found it.',
  },
  {
    id: 'oura',
    icon: Activity,
    eyebrow: 'Body-aware coaching',
    title: 'A coach that knows when you slept badly.',
    body: 'Connect an Oura ring and SAGE gently factors in your sleep, readiness and recovery, so a hard day gets a softer plan.',
    moment: 'Arriving in a future wave.',
    soon: true,
  },
];

const FeatureCard = ({ f, index }) => {
  const Icon = f.icon;
  return (
    <div
      data-reveal
      style={{ transitionDelay: `${index * 90}ms` }}
      className={`group relative ${f.soon ? 'md:col-span-2' : ''} bg-white/[0.02] border border-white/10 hover:border-white/25 rounded-sm p-8 transition-colors duration-500`}
      data-testid={`welcome-feature-${f.id}`}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 text-amber-200/80 welcome-accent group-hover:text-amber-200 transition-colors">
          <span className="w-9 h-9 rounded-sm border border-white/15 flex items-center justify-center">
            <Icon className="w-5 h-5" aria-hidden="true" />
          </span>
          <span className="text-[11px] font-mono uppercase tracking-[0.25em]">{f.eyebrow}</span>
        </div>
        {f.soon && (
          <span className="text-[10px] font-mono uppercase tracking-widest text-amber-200 welcome-accent border border-current rounded-full px-3 py-1" data-testid="welcome-oura-soon">
            Coming soon
          </span>
        )}
      </div>
      <h3 className="text-2xl md:text-3xl font-light text-zinc-100 mb-3 leading-snug" style={serif}>{f.title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed mb-5">{f.body}</p>
      <p className="text-sm italic text-zinc-300 border-l border-amber-200/40 pl-4" style={serif}>{f.moment}</p>
    </div>
  );
};

const WaitSteps = () => (
  <ol className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="welcome-steps">
    {[
      ['Your place is saved', 'Your request is in. Nothing more to do right now.'],
      ['Invites open in small waves', 'We welcome a few new members at a time so every first session gets real care.'],
      ['Your invitation arrives', 'When your wave opens, we email you a personal code. Then the path is yours.'],
    ].map(([title, body], i) => (
      <li key={title} data-reveal style={{ transitionDelay: `${i * 120}ms` }} className="border-t border-white/15 pt-6">
        <span className="text-[11px] font-mono text-amber-200/70 welcome-accent tracking-widest">0{i + 1}</span>
        <h4 className="text-xl font-light text-zinc-100 mt-2 mb-2" style={serif}>{title}</h4>
        <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
      </li>
    ))}
  </ol>
);

export default function BetaWelcomePage() {
  const navigate = useNavigate();
  const email = useLocation().state?.email;

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('reveal-visible')),
      { threshold: 0.12 },
    );
    document.querySelectorAll('[data-reveal]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen relative bg-obsidian text-zinc-100" data-testid="beta-welcome-page">
      <div className="overlay-noise fixed inset-0 pointer-events-none z-0" />
      <div className="fixed top-5 right-5 z-30">
        <ThemeToggle size="sm" className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm" />
      </div>

      <section className="relative min-h-[88vh] flex items-center justify-center px-6 py-24 overflow-hidden" data-testid="welcome-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          data-testid="landing-tree-bg"
          style={{ backgroundImage: "linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,0.78) 60%, rgba(10,10,10,0.97) 100%), url('/landing-tree-fog.jpg')" }}
        />
        <div className="relative z-10 max-w-3xl text-center animate-in fade-in slide-in-from-bottom-4 duration-1000">
          <span className="mx-auto mb-8 w-14 h-14 rounded-full border border-current flex items-center justify-center text-amber-200 welcome-accent welcome-pulse" data-testid="welcome-check">
            <Check className="w-6 h-6" aria-hidden="true" />
          </span>
          <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-amber-200/80 welcome-accent mb-6">Request received</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-zinc-100 mb-6" style={serif} data-testid="welcome-title">
            You&rsquo;re on the path.
          </h1>
          <p className="text-xl md:text-2xl font-light text-zinc-200 leading-relaxed mb-4" style={serif} data-testid="welcome-message">
            Thank you for raising your hand. We&rsquo;ve saved your place
            {email ? <> for <span className="italic text-white" data-testid="welcome-email">{email}</span></> : null}.
          </p>
          <p className="text-sm md:text-base text-zinc-400 max-w-xl mx-auto leading-relaxed">
            Invites go out in small waves. While you wait, here&rsquo;s a glimpse of the next
            generation of coaching and learning that&rsquo;s waiting for you.
          </p>
        </div>
      </section>

      <section className="relative z-10 py-24 px-6 border-t border-white/5" data-testid="welcome-features">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-16" data-reveal>
            <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-amber-200/60 welcome-accent mb-4">What&rsquo;s waiting for you</p>
            <h2 className="text-4xl md:text-5xl font-light text-zinc-100 leading-tight" style={serif}>
              Not another app. A companion that grows with you.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURES.map((f, i) => <FeatureCard key={f.id} f={f} index={i} />)}
          </div>
        </div>
      </section>

      <section className="relative z-10 py-24 px-6 border-t border-white/5" data-testid="welcome-next">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] font-mono uppercase tracking-[0.35em] text-amber-200/60 welcome-accent mb-10" data-reveal>What happens next</p>
          <WaitSteps />
          <div className="mt-20 flex flex-col sm:flex-row gap-4 items-start sm:items-center" data-reveal>
            <button type="button" onClick={() => navigate('/')} className="threshold-button rounded-sm px-8 py-4 text-base" data-testid="welcome-home-button">
              <span>Back to SAGE</span>
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => navigate('/login')} className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors" data-testid="welcome-signin-link">
              Already have your code? Sign in →
            </button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
